import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HKBillingPeriod } from 'core/constants';

export interface PeriodPricing {
  addon_code: string | null;
  base_code: string;
  cad: number | null;
  usd: number;
}

export interface PlanPricing {
  monthly: PeriodPricing;
  yearly: PeriodPricing;
}

export interface AddOnTextPricing {
  price_text: string;
}

export interface PlanAddon {
  active: boolean;
  bullets: string[];
  description: string;
  id: number;
  name: string;
  pricing: AddOnTextPricing | PlanPricing;
  type: string;
  type_id: number;
  typed: number;
}

export interface SubscriptionPlan {
  active: boolean;
  addons: PlanAddon[];
  bullets: string[];
  description: string;
  id: number;
  name: string;
  pricing: PlanPricing;
  purchase_end: string | null;
  renew_end: string | null;
  square_feet_max: number | null;
  square_feet_min: number | null;
  type: string;
  type_id: number;
  typed: number;
}

export interface CurrentPlan {
  subscription_plan: number;
  promo_coupon: {
    description: string;
    recurly_code: string;
  } | null;
  [key: string]: unknown;
}

interface SubscriptionPlansResponse {
  available_plans: SubscriptionPlan[];
  current_plans: CurrentPlan[];
}

interface BillingState {
  loading: boolean;
  error: any;
  availablePlans: SubscriptionPlan[];
  availableAddOns: PlanAddon[];
  currentPlans: CurrentPlan[];
  currentBillingInfo: any;
  promoCode: string;
  newAddOns: number[];
  newPlan: any;
  newCreditCard: any;
  newBillingPeriod: HKBillingPeriod;
  autoRenew: boolean;
  addOnModalVisible: boolean;
  subscriptionSummaryModalVisible: boolean;
  billingFirstName: string;
  billingLastName: string;
  billingAddress1: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingCountry: string;
  showCreditCardForm: boolean;
}

export const initialState: BillingState = {
  loading: false,
  error: null,
  availablePlans: [],
  availableAddOns: [],
  currentPlans: [],
  currentBillingInfo: null,
  promoCode: '',
  newAddOns: [],
  newPlan: null,
  newCreditCard: null,
  newBillingPeriod: HKBillingPeriod.MONTHLY,
  autoRenew: true,
  addOnModalVisible: false,
  subscriptionSummaryModalVisible: false,
  billingFirstName: '',
  billingLastName: '',
  billingAddress1: '',
  billingCity: '',
  billingState: '',
  billingPostalCode: '',
  billingCountry: '',
  showCreditCardForm: false
};

export const BillingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    fetchSubscriptionPlansStart: (state) => {
      state.loading = true;
    },
    fetchSubscriptionPlansSuccess: (
      state,
      action: PayloadAction<SubscriptionPlansResponse>
    ) => {
      state.availablePlans = action.payload.available_plans;
      state.currentPlans = action.payload.current_plans;
      state.loading = false;
    },
    fetchSubscriptionPlansFailure: (state) => {
      state.availablePlans = [];
      state.loading = false;
    },
    fetchCreditCardInfoStart: (state, action: PayloadAction<any>) => {
      state.loading = true;
    },
    fetchCreditCardInfoSuccess: (state, action: PayloadAction<any>) => {
      if (Object.keys(action.payload.billing_info).length !== 0) {
        state.currentBillingInfo = action.payload.billing_info;
      }
      state.loading = false;
    },
    fetchCreditCardInfoFailure: (state, action: PayloadAction<any>) => {
      state.loading = false;
    },
    renewSubscriptionStart: (state, action: PayloadAction<any>) => {
      state.loading = true;
    },
    renewSubscriptionSuccess: (state, action: PayloadAction<any>) => {
      if (
        !!action.payload.billing_info &&
        Object.keys(action.payload.billing_info).length !== 0
      ) {
        state.currentBillingInfo = action.payload.billing_info;
      }
      state.loading = false;
    },
    renewSubscriptionFailure: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    updateSubscriptionStart: (state, action: PayloadAction<any>) => {
      state.loading = true;
    },
    updateSubscriptionSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
    },
    updateSubscriptionFailure: (state, action: PayloadAction<any>) => {
      state.loading = false;
    },
    deleteSubscriptionStart: (state, action: PayloadAction<any>) => {
      state.loading = true;
    },
    deleteSubscriptionSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
    },
    deleteSubscriptionFailure: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    removeSavedCard: (state) => {
      state.currentBillingInfo = null;
    },
    setPromoCode: (state, action: PayloadAction<any>) => {
      state.promoCode = action.payload;
      state.loading = false;
    },
    setNewBillingPeriod: (state, action: PayloadAction<any>) => {
      state.newBillingPeriod = action.payload;
      state.loading = false;
    },
    setNewCreditCard: (state, action: PayloadAction<any>) => {
      state.newCreditCard = action.payload;
      state.loading = false;
    },
    setNewAddOns: (state, action: PayloadAction<any>) => {
      state.newAddOns = [];
      state.newAddOns = action.payload;
      state.loading = false;
    },
    setNewPlan: (state, action: PayloadAction<any>) => {
      state.newPlan = action.payload;
      state.loading = false;
    },

    setAutoRenew: (state, action: PayloadAction<any>) => {
      state.autoRenew = action.payload;
    },
    setBillingFirstName: (state, action: PayloadAction<any>) => {
      state.billingFirstName = action.payload;
    },
    setBillingLastName: (state, action: PayloadAction<any>) => {
      state.billingLastName = action.payload;
    },
    setBillingAddress1: (state, action: PayloadAction<any>) => {
      state.billingAddress1 = action.payload;
    },
    setBillingCity: (state, action: PayloadAction<any>) => {
      state.billingCity = action.payload;
    },
    setBillingState: (state, action: PayloadAction<any>) => {
      state.billingState = action.payload;
    },
    setBillingPostalCode: (state, action: PayloadAction<any>) => {
      state.billingPostalCode = action.payload;
    },
    setBillingCountry: (state, action: PayloadAction<any>) => {
      state.billingCountry = action.payload;
    },
    setShowCreditCardForm: (state, action: PayloadAction<any>) => {
      state.showCreditCardForm = action.payload;
    },
    showAddOnsModal: (state, action: PayloadAction<any>) => {
      state.availableAddOns = action.payload;
      state.addOnModalVisible = true;
    },
    hideAddOnsModal: (state) => {
      state.availableAddOns = [];
      state.addOnModalVisible = false;
    },
    showSubscriptionSummaryModal: (state) => {
      state.subscriptionSummaryModalVisible = true;
    },
    hideSubscriptionSummaryModal: (state) => {
      state.subscriptionSummaryModalVisible = false;
    },
    resetBilling: () => initialState
  }
});

export const {
  fetchSubscriptionPlansStart,
  fetchSubscriptionPlansSuccess,
  fetchSubscriptionPlansFailure,
  fetchCreditCardInfoStart,
  fetchCreditCardInfoSuccess,
  fetchCreditCardInfoFailure,
  renewSubscriptionStart,
  renewSubscriptionSuccess,
  renewSubscriptionFailure,
  updateSubscriptionStart,
  updateSubscriptionSuccess,
  updateSubscriptionFailure,
  deleteSubscriptionStart,
  deleteSubscriptionSuccess,
  deleteSubscriptionFailure,
  removeSavedCard,
  setPromoCode,
  setNewBillingPeriod,
  setNewCreditCard,
  setNewAddOns,
  setNewPlan,
  setAutoRenew,
  setBillingFirstName,
  setBillingLastName,
  setBillingAddress1,
  setBillingCity,
  setBillingState,
  setBillingPostalCode,
  setBillingCountry,
  setShowCreditCardForm,
  showAddOnsModal,
  hideAddOnsModal,
  showSubscriptionSummaryModal,
  hideSubscriptionSummaryModal,
  resetBilling
} = BillingSlice.actions;

export default BillingSlice.reducer;
